import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo
} from "react";
import ReactDOM from "react-dom";
import {
  fetchFilteredPhotos,
  fetchFilterOptions,
  FilterOptionsResponse,
  LibraryPhoto,
  EntityType
} from "../PhotoManager/API/api";
import BundleMediaSelect from "../BrowseEvent/components/albums/BundleSelectMedia";
import DeletePhotosConfirmation from "../BrowseEvent/components/DeletePhotosConfirmation";
import PhotoPopup from "../PhotoManager/components/PhotoPopup";
import { PhotoManagerContext } from "../PhotoManager/components/index";
import {
  PhotoType,
  VideoType,
  Album,
  EventType,
  ParticipantObject
} from "../PhotoManager/components/types";
import MobileFiltersModal from "./components/MobileFiltersModal";
import {
  SearchFilters,
  DEFAULT_FILTERS,
  hasActiveFilters
} from "./filters/filterTypes";
import undoIcon from "./style/undo-icon.svg";
import { calculatePhotoDateRange } from "./utils/dateRangeUtils";
import HeaderSection from "./components/HeaderSection";
import GenerateShowreelModal from "./components/GenerateShowreelModal";
import ActionBar from "./components/ActionBar";
import PhotoGrid from "./components/PhotoGrid";
import FiltersBar from "./components/FiltersBar";
import { AlbumGroup, EventGroup, ActiveDropdown } from "./components/types";

const geoLoader = require("../../assets/gss-loader.gif");

interface FilteredPhotosPageProps {
  entityType: EntityType;
  entityId: number;
  entityName: string;
  onBack: () => void;
  selfieSearchS3Bucket?: string;
  currentUserId?: number;
  redactionCheckEnabled?: boolean;
  canGenerateShowreels?: boolean;
  organizationId?: number;
}

const libraryPhotoToPhotoType = (
  photo: LibraryPhoto,
  isHidden?: boolean
): PhotoType | VideoType => {
  const hasPermissions =
    photo.permissions?.byAttribute && photo.permissions.byAttribute.length > 0;
  const permissions =
    hasPermissions && photo.permissions
      ? photo.permissions.byAttribute.map(attr => ({
          label: attr.attributeLabel,
          value: attr.value
        }))
      : [];

  const hasPeople = photo.people && photo.people.length > 0;
  const participants: ParticipantObject[] = hasPeople
    ? photo.people.map(person => {
        const firstName = person.firstName || "";
        const lastName = person.lastName || "";
        const fullName =
          [firstName, lastName].filter(Boolean).join(" ") ||
          person.extId ||
          "Unnamed Person";

        const participant: ParticipantObject = {
          id: person.id,
          name: fullName,
          photoURL: person.photoURL ?? "",
          permissions: person.permissions || [],
          redacted: person.redacted ?? false,
          permissionExpiryStatus: person.permissionExpiryStatus
            ? {
                status: person.permissionExpiryStatus.status,
                message: person.permissionExpiryStatus.message ?? undefined
              }
            : null
        };

        if (person.photoPermissionExpiresOn) {
          participant.photoPermissionExpiresOn =
            person.photoPermissionExpiresOn;
        }
        if (person.className) {
          participant.className = person.className;
        }

        return participant;
      })
    : [];

  const base = {
    id: photo.id,
    thumbnail: photo.thumbnailURL,
    zoomImg: photo.fullURL,
    largeImg: photo.fullURL,
    watermarkedImg: null,
    animatedGif: null,
    contentType: photo.contentType,
    country: null,
    dateTimeTaken: photo.dateTimeTaken,
    timeTaken: photo.timeTaken,
    favorite: null,
    fetchAppliedTagsPath: "",
    filename: photo.fileName,
    hidden: isHidden !== undefined ? isHidden : photo.hidden,
    hidePhotographersFilter: false,
    redacted: photo.permissions?.redacted || false,
    redactionChecked: photo.redactionChecked,
    flaggedForModeration: photo.flaggedForModeration ?? false,
    reportPhotoPath: "",
    tags: photo.tags.map(t => t.name),
    thumbnailHeight: null,
    thumbnailWidth: null,
    type: "Photo" as const,
    photographerName: "",
    device: "",
    contentTagsAPI: "/api/v1/content_tags",
    permissions,
    participants,
    redactions: [],
    canTag: photo.canTag,
    canHide: photo.canHide,
    canDelete: photo.canDelete
  };

  if (
    photo.videoContent &&
    photo.videoMuxPlaybackId != null &&
    photo.videoMuxId != null &&
    photo.videoDuration != null
  ) {
    return {
      ...base,
      videoContent: true,
      videoDuration: photo.videoDuration,
      videoMuxId: photo.videoMuxId,
      videoMuxPlaybackId: photo.videoMuxPlaybackId,
      videoStartTime: photo.videoStartTime ?? 0
    };
  }

  return {
    ...base,
    videoContent: false,
    videoDuration: null,
    videoMuxId: null,
    videoMuxPlaybackId: null,
    videoStartTime: photo.videoStartTime ?? 0
  };
};

const buildEventStub = (photo: LibraryPhoto): EventType => ({
  id: photo.eventId,
  name: photo.eventName,
  referenceId: null,
  hierarchy: [],
  libraryFolderId: null,
  parentFolderId: null,
  currency: "",
  waitingCustomerLeadsCount: 0,
  slug: "",
  startDate: "",
  endDate: "",
  organiserEmail: "",
  organiserFirstName: "",
  isReference: false,
  location: "",
  canAccessManageEventPage: false,
  coverPhoto: "",
  browsePhotosURL: "",
  uploadPhotosPath: "",
  manageEventPath: "",
  markUploadCompleteURL: "",
  uploadCompleteStatus: false,
  price: 0,
  password: null,
  publishMode: "",
  displayPrice: "",
  date: "",
  isProtected: false,
  isAuthorizedForDirectDownloads:
    photo.eventPermissions?.isAuthorizedForDirectDownloads ?? false,
  albumsCount: 0,
  tags: [],
  type: "Event"
});

const buildAlbumStub = (photo: LibraryPhoto): Album => ({
  id: photo.albumId,
  name: photo.albumName,
  slug: "",
  type: "Album",
  photosCount: 0,
  eventId: photo.eventId,
  eventName: photo.eventName,
  signedId: "",
  photographerName: "",
  photographerEmail: "",
  photographerId: 0,
  tags: [],
  thumbnailURL: null,
  albumPath: "",
  uploadPhotosPath: "",
  editAlbumPath: "",
  shareAlbumURL: "",
  eventData: buildEventStub(photo),
  hasVideoAccess: false,
  mobileCameraOnlyUpload: false,
  canCurrentUserDelete: photo.albumPermissions?.canCurrentUserDelete ?? false,
  canRename: false,
  canUpload: false,
  optedForFaceIndexing: false,
  canUpdateSelfiePreference: false,
  free: false,
  canUploadToFreeEvent: false,
  hasFreeSharingSubscription: false,
  canManageFreeSharingSubscription: false,
  canCreateFreeSharingSubscription: false,
  createSubscriptionPath: "",
  manageSubscriptionPath: "",
  groupPhotos: false,
  appliedTags: [],
  canManageTags: false,
  contentTagsApi: "/api/v1/content_tags",
  organizationId: 0
});

const FilteredPhotosPage: React.FC<FilteredPhotosPageProps> = ({
  entityType,
  entityId,
  entityName,
  onBack,
  selfieSearchS3Bucket = "geosnapshot-selfie-search",
  currentUserId: currentUserIdProp,
  redactionCheckEnabled,
  canGenerateShowreels = false,
  organizationId
}) => {
  const [photos, setPhotos] = useState<LibraryPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(new Set());
  const [showTagModal, setShowTagModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState<
    PhotoType | VideoType | null
  >(null);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);
  const [lightboxPhotos, setLightboxPhotos] = useState<
    Array<PhotoType | VideoType>
  >([]);
  const [lightboxEvent, setLightboxEvent] = useState<EventType | null>(null);
  const [lightboxAlbum, setLightboxAlbum] = useState<Album | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showShowreelModal, setShowShowreelModal] = useState(false);

  // Check for selfie search from URL params once
  const selfieImageFromUrl = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const selfieImage = urlParams.get("selfie_image");
    return selfieImage ? decodeURIComponent(selfieImage) : null;
  }, []);

  const [filters, setFilters] = useState<SearchFilters>(() => DEFAULT_FILTERS);
  const [activeDropdown, setActiveDropdown] = useState<ActiveDropdown>(null);
  const [loadingFilter, setLoadingFilter] = useState<ActiveDropdown>(null);
  const [loadingItemId, setLoadingItemId] = useState<number | null>(null);
  const [loadingPermissionKey, setLoadingPermissionKey] = useState<
    string | null
  >(null);
  const [filterOptions, setFilterOptions] =
    useState<FilterOptionsResponse | null>(null);
  const [allEvents, setAllEvents] = useState<
    Array<{ id: number; name: string; photo_count: number }>
  >([]);
  const [allTags, setAllTags] = useState<
    Array<{ id: number; name: string; photo_count: number }>
  >([]);
  const [allPeople, setAllPeople] = useState<
    Array<{ id: number; name: string; photo_count: number }>
  >([]);
  const [availablePermissionAttributes, setAvailablePermissionAttributes] =
    useState<Array<{ attributeId: number; attributeLabel: string }>>([]);
  const [hasRedactedParticipants, setHasRedactedParticipants] =
    useState(false);
  const [hasMatchedPeople, setHasMatchedPeople] =
    useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const parentContext = useContext(PhotoManagerContext);

  // Local mobile detection fallback when PhotoManagerContext is not available
  const [localIsMobile, setLocalIsMobile] = useState(
    () => window.innerWidth < 992
  );

  useEffect(() => {
    // Only set up resize listener if we're using local detection (no parent context)
    if (parentContext?.isMobile !== undefined) {
      return;
    }

    const handleResize = () => {
      setLocalIsMobile(window.innerWidth < 992);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [parentContext]);

  const getCurrentUserId = (): number => {
    if (currentUserIdProp) {
      return currentUserIdProp;
    }
    if (parentContext?.currentUserId) {
      return parentContext.currentUserId;
    }
    const metaTag = document.querySelector('meta[name="current-user-id"]');
    if (metaTag) {
      return parseInt(metaTag.getAttribute("content") || "0", 10);
    }
    return 0;
  };

  const [currentUserId] = useState(getCurrentUserId());

  const availableTags = useMemo(
    () =>
      allTags.filter(
        (t: (typeof allTags)[number]) =>
          !(entityType === "tag" && t.id === entityId)
      ),
    [allTags, entityType, entityId]
  );

  const availableEvents = useMemo(
    () =>
      allEvents.filter(
        (e: (typeof allEvents)[number]) =>
          !(entityType === "event" && e.id === entityId)
      ),
    [allEvents, entityType, entityId]
  );

  const availablePeople = useMemo(
    () =>
      allPeople.filter(
        (p: (typeof allPeople)[number]) =>
          !(entityType === "person" && p.id === entityId)
      ),
    [allPeople, entityType, entityId]
  );

  const photoDateRange = useMemo(
    () => calculatePhotoDateRange(photos),
    [photos]
  );

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options =
          entityId === -1
            ? await fetchFilterOptions(
                undefined,
                undefined,
                selfieImageFromUrl || undefined
              )
            : await fetchFilterOptions(entityType, entityId);
        setFilterOptions(options);

        if (options.events) {
          const sortedEvents = options.events.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setAllEvents(sortedEvents);
        }

        if (options.tags) {
          const sortedTags = options.tags.sort(
            (a, b) => b.photo_count - a.photo_count
          );
          setAllTags(sortedTags);
        }

        if (options.people) {
          setAllPeople(options.people);
        }

        if (options.photo_permission_attributes) {
          setAvailablePermissionAttributes(
            options.photo_permission_attributes.map(attr => ({
              attributeId: attr.id,
              attributeLabel: attr.label
            }))
          );
        }

        setHasRedactedParticipants(!!options.has_redacted_participants);
        setHasMatchedPeople(!!options.has_matched_people);
      } catch (err) {
        console.error("Failed to load filter options:", err);
      }
    };

    loadFilterOptions();
  }, [entityType, entityId, selfieImageFromUrl]);

  useEffect(() => {
    loadPhotos();
  }, [entityType, entityId, page, filters]);

  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
    setHasLoadedOnce(false);
    if (!selfieImageFromUrl) {
      setFilters(DEFAULT_FILTERS);
    }
  }, [entityType, entityId, selfieImageFromUrl]);

  const loadPhotos = async () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    const controller = abortRef.current;

    try {
      setIsLoading(true);
      setError(null);

      const {
        photoPermissions,
        dateRange,
        selectedEventIds,
        selectedTagIds,
        selectedPersonIds,
        selfie
      } = filters;

      const permissionFilters = photoPermissions.permissions
        .filter(p => p.value !== "all")
        .map(p => ({
          attribute_id: p.attributeId,
          value: p.value === "yes" ? ("true" as const) : ("false" as const)
        }));

      const redactedFilter =
        photoPermissions.redactedPhoto !== "all"
          ? photoPermissions.redactedPhoto === "yes"
            ? ("true" as const)
            : ("false" as const)
          : undefined;

      const response = await fetchFilteredPhotos(
        entityType,
        entityId,
        {
          page,
          per_page: 100,
          ...(selectedTagIds.length > 0 && { tag_ids: selectedTagIds }),
          ...(selectedEventIds.length > 0 && { event_ids: selectedEventIds }),
          ...(selectedPersonIds.length > 0 && {
            person_ids: selectedPersonIds
          }),
          ...(dateRange.startDate && { date_from: dateRange.startDate }),
          ...(dateRange.endDate && { date_to: dateRange.endDate }),
          ...(selfieImageFromUrl && { reference_image: selfieImageFromUrl }),
          ...(selfie.imageUrl &&
            selfieImageFromUrl && { filter_reference_image: selfie.imageUrl }),
          ...(!selfieImageFromUrl &&
            selfie.imageUrl && { reference_image: selfie.imageUrl }),
          ...(permissionFilters.length > 0 && {
            permission_filters: permissionFilters
          }),
          ...(redactedFilter && { redacted_photo: redactedFilter })
        },
        controller.signal
      );

      if (abortRef.current !== controller) return;

      if (page === 1) {
        setPhotos(response.photos);
        setHiddenIds(
          new Set(response.photos.filter(p => p.hidden).map(p => p.id))
        );
      } else {
        setPhotos(prev => [...prev, ...response.photos]);
        setHiddenIds(prev => {
          const next = new Set(prev);
          response.photos
            .filter(p => p.hidden)
            .forEach(p => {
              next.add(p.id);
            });
          return next;
        });
      }
      setHasMore(response.meta.hasMore);
      setTotalCount(response.meta.totalCount || 0);
      setHasLoadedOnce(true);
    } catch (err: any) {
      if (err?.name === "AbortError") return;

      if (abortRef.current !== controller) return;

      setError("Failed to load photos. Please try again.");
      console.error("Error loading photos:", err);
    } finally {
      if (abortRef.current === controller) {
        setIsLoading(false);
        setLoadingFilter(null);
        setLoadingItemId(null);
        setLoadingPermissionKey(null);
      }
    }
  };

  const applyFilter = useCallback(
    (
      key: keyof SearchFilters,
      value: any,
      options?: {
        filterName?: ActiveDropdown;
        itemId?: number;
        permissionKey?: string;
      }
    ) => {
      setFilters(prev => ({ ...prev, [key]: value }));
      setPage(1);
      setSelectedIds(new Set());
      setLoadingFilter(options?.filterName || null);
      setLoadingItemId(options?.itemId ?? null);
      setLoadingPermissionKey(options?.permissionKey || null);
    },
    []
  );

  const toggleDropdown = (name: ActiveDropdown) => {
    setActiveDropdown(prev => (prev === name ? null : name));
  };

  const getEntityTypeLabel = (): string => {
    switch (entityType) {
      case "tag":
        return "Tag";
      case "person":
        return "Person";
      case "group":
        return "Group";
      case "event":
        return "Event";
      case "album":
        return "Album";
      case "folder":
        return "Folder";
      default:
        return "";
    }
  };

  const togglePhotoSelection = useCallback((photoId: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(photoId)) {
        next.delete(photoId);
      } else {
        next.add(photoId);
      }
      return next;
    });
  }, []);

  const showActionTooltip = useCallback(
    (id: number, title: string, type: string) => {
      const element = document.getElementById(`${type}-icon-${id}`);
      if (element && (window as any).$) {
        (window as any)
          .$(element)
          .tooltip({
            title,
            html: true,
            placement: "top"
          })
          .tooltip("show");
      }
    },
    []
  );

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedIds(
        event.target.checked ? new Set(photos.map(p => p.id)) : new Set()
      );
    },
    [photos]
  );

  const isPhotoHidden = (id: number) => hiddenIds.has(id);

  const allSelected = photos.length > 0 && selectedIds.size === photos.length;
  const someSelected = selectedIds.size > 0;

  const hideOrUnhideText = () => {
    const shouldHide = Array.from(selectedIds).some(id => !isPhotoHidden(id));
    return shouldHide ? "hide" : "unhide";
  };

  const handleHide = async () => {
    const selectedPhotosList = photos.filter(p => selectedIds.has(p.id));
    const allHidden = selectedPhotosList.every(p => isPhotoHidden(p.id));
    const action = allHidden ? "unhide" : "hide";

    const photosByEvent = selectedPhotosList.reduce((acc, photo) => {
      if (!acc[photo.eventId]) {
        acc[photo.eventId] = [];
      }
      acc[photo.eventId].push(photo.id);
      return acc;
    }, {} as Record<number, number[]>);

    try {
      await Promise.all(
        Object.entries(photosByEvent).map(([eventId, eventPhotoIds]) =>
          (window as any).$.ajax({
            url: `/api/v1/photos/${action}`,
            type: "POST",
            data: { photo_ids: eventPhotoIds, event_id: parseInt(eventId, 10) }
          })
        )
      );

      setHiddenIds(prev => {
        const next = new Set(prev);
        selectedIds.forEach(id => {
          action === "hide" ? next.add(id) : next.delete(id);
        });
        return next;
      });
      // @ts-ignore
      toastr.success(
        `Photos ${action === "hide" ? "hidden" : "unhidden"} successfully`
      );
      setSelectedIds(new Set());
    } catch (err) {
      // @ts-ignore
      toastr.error("Failed to update photos. Please try again.");
      console.error(err);
    }
  };

  const handleDelete = async () => {
    const selectedPhotosList = photos.filter(p => selectedIds.has(p.id));

    const photosByAlbum = selectedPhotosList.reduce((acc, photo) => {
      if (!acc[photo.albumId]) {
        acc[photo.albumId] = [];
      }
      acc[photo.albumId].push(photo.id);
      return acc;
    }, {} as Record<number, number[]>);

    setIsDeleting(true);
    try {
      const deletePromises = Object.entries(photosByAlbum).map(
        ([albumId, photoIds]) =>
          (window as any).$.ajax({
            url: `/api/v1/photo-manager/delete_media_of_album`,
            type: "DELETE",
            data: { photo_ids: photoIds, album_id: parseInt(albumId, 10) }
          })
      );

      await Promise.all(deletePromises);

      // @ts-ignore
      toastr.success("Photos deleted successfully");
      setShowDeleteModal(false);
      setSelectedIds(new Set());
      setPhotos(prev => prev.filter(p => !selectedIds.has(p.id)));
    } catch (err) {
      // @ts-ignore
      toastr.error("Failed to delete photos. Please try again.");
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async () => {
    if (selectedIds.size === 0) return;

    setIsDownloading(true);

    try {
      const response = await fetch("/api/v1/photo-manager/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photo_ids: Array.from(selectedIds) })
      });
      if (!response.ok) throw new Error("Failed to fetch download URL");
      const data = await response.json();
      window.location.href = data.download_url;
    } catch (err) {
      // @ts-ignore
      toastr.error("Failed to download photos. Please try again.");
      console.error(err);
    } finally {
      setIsDownloading(false);
      setSelectedIds(new Set());
    }
  };

  const groupedByEvent = (): EventGroup[] => {
    const eventMap = new Map<number, EventGroup>();
    photos.forEach(photo => {
      if (!eventMap.has(photo.eventId)) {
        eventMap.set(photo.eventId, {
          eventId: photo.eventId,
          eventName: photo.eventName,
          albums: []
        });
      }
      const event = eventMap.get(photo.eventId)!;
      let album = event.albums.find(a => a.albumId === photo.albumId);
      if (!album) {
        album = {
          albumId: photo.albumId,
          albumName: photo.albumName,
          photos: []
        };
        event.albums.push(album);
      }
      album.photos.push(photo);
    });
    return Array.from(eventMap.values());
  };

  const handlePhotoClick = (
    photo: LibraryPhoto,
    albumPhotos: LibraryPhoto[],
    indexInAlbum: number
  ) => {
    const mapped = albumPhotos.map(p =>
      libraryPhotoToPhotoType(p, isPhotoHidden(p.id))
    );
    setLightboxPhotos(mapped);
    setLightboxPhoto(mapped[indexInAlbum]);
    setLightboxIndex(indexInAlbum);
    setLightboxEvent(buildEventStub(photo));
    setLightboxAlbum(buildAlbumStub(photo));
  };

  const handleLightboxNavigate = (index: number) => {
    setLightboxIndex(index);
    setLightboxPhoto(lightboxPhotos[index]);
  };

  const handleCloseLightbox = () => {
    setLightboxPhoto(null);
    setLightboxPhotos([]);
    setLightboxEvent(null);
    setLightboxAlbum(null);
  };

  const navigateToEvent = (eventId: number) => {
    window.location.href = `/library?p=${btoa(`g:${eventId}`)}`;
  };

  const navigateToAlbum = (eventId: number, albumId: number) => {
    window.location.href = `/library?p=${btoa(`g:${eventId}/a:${albumId}`)}`;
  };

  const permissionsActive =
    filters.photoPermissions.permissions.some(p => p.value !== "all") ||
    filters.photoPermissions.redactedPhoto !== "all";
  const showPermissionsFilter =
    hasRedactedParticipants || availablePermissionAttributes.length > 0;
  const showPeopleFilter = hasMatchedPeople;
  const dateActive = filters.dateRange.startDate !== null;
  const eventActive = filters.selectedEventIds.length > 0;
  const tagActive = filters.selectedTagIds.length > 0;
  const peopleActive = filters.selectedPersonIds.length > 0;
  const selfieActive = filters.selfie.imageUrl !== null;

  const activeFilterCount = [
    peopleActive,
    permissionsActive,
    dateActive,
    selfieActive && filters.selfie.imageUrl !== selfieImageFromUrl,
    eventActive,
    tagActive
  ].filter(Boolean).length;

  const isGlobalSelfieSearch = entityId === -1 && selfieImageFromUrl !== null;

  const hasUserAppliedFilters = useCallback(() => {
    if (isGlobalSelfieSearch) {
      return (
        filters.selectedEventIds.length > 0 ||
        filters.selectedTagIds.length > 0 ||
        filters.selectedPersonIds.length > 0 ||
        filters.dateRange.startDate !== null ||
        filters.dateRange.endDate !== null ||
        (filters.selfie.imageUrl !== null &&
          filters.selfie.imageUrl !== selfieImageFromUrl) ||
        filters.photoPermissions.permissions.some(p => p.value !== "all") ||
        filters.photoPermissions.redactedPhoto !== "all"
      );
    }
    return hasActiveFilters(filters);
  }, [isGlobalSelfieSearch, filters, selfieImageFromUrl]);

  const displayedPhotosCount = photos.length;

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
    setSelectedIds(new Set());
  };

  const buildShowreelSearchParams = (): Record<string, any> => {
    const {
      photoPermissions,
      dateRange,
      selectedEventIds,
      selectedTagIds,
      selectedPersonIds,
      selfie
    } = filters;

    const permissionFilters = photoPermissions.permissions
      .filter(p => p.value !== "all")
      .map(p => ({
        attribute_id: p.attributeId,
        value: p.value === "yes" ? "true" : "false"
      }));

    const redactedFilter =
      photoPermissions.redactedPhoto !== "all"
        ? photoPermissions.redactedPhoto === "yes"
          ? "true"
          : "false"
        : undefined;

    return {
      ...(entityId !== -1 && {
        entity_type: entityType,
        entity_id: entityId
      }),
      ...(selectedTagIds.length > 0 && { tag_ids: selectedTagIds }),
      ...(selectedEventIds.length > 0 && { event_ids: selectedEventIds }),
      ...(selectedPersonIds.length > 0 && { person_ids: selectedPersonIds }),
      ...(dateRange.startDate && { date_from: dateRange.startDate }),
      ...(dateRange.endDate && { date_to: dateRange.endDate }),
      ...(selfieImageFromUrl && { reference_image: selfieImageFromUrl }),
      ...(selfie.imageUrl &&
        selfieImageFromUrl && { filter_reference_image: selfie.imageUrl }),
      ...(!selfieImageFromUrl &&
        selfie.imageUrl && { reference_image: selfie.imageUrl }),
      ...(permissionFilters.length > 0 && {
        permission_filters: permissionFilters
      }),
      ...(redactedFilter && { redacted_photo: redactedFilter })
    };
  };

  if (isLoading && !hasLoadedOnce) {
    return (
      <div className="geo-manager-content">
        <div className="filtered-photos-loading">
          <img src={geoLoader} alt="Loading..." className="geo-loader" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="geo-manager-content">
        <div className="filtered-photos-error">
          <p>{error}</p>
          <button onClick={loadPhotos} className="button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const eventGroups = groupedByEvent();
  // Prefer parent context isMobile if available, otherwise use local detection
  const isMobile = parentContext?.isMobile ?? localIsMobile;
  const selectedMediaList = photos
    .filter(p => selectedIds.has(p.id))
    .map(p => ({ id: p.id, hidden: p.hidden }));

  return (
    <div className="geo-manager-content">
      <HeaderSection
        onBack={onBack}
        selfieImageFromUrl={selfieImageFromUrl}
        totalCount={totalCount}
        entityType={entityType}
        entityName={entityName}
        action={
          canGenerateShowreels &&
          photos.length > 0 &&
          (entityType === "person" ||
            !!selfieImageFromUrl ||
            !!filters.selfie.imageUrl) ? (
            <button
              type="button"
              className="geo-btn geo-btn--primary geo-btn--caps generate-showreel-btn"
              onClick={() => setShowShowreelModal(true)}
            >
              <i className="fa fa-film"></i>
              Create showreel
            </button>
          ) : undefined
        }
      />

      <div className="filtered-filters-row">
        <span className="filters-label">FILTERS</span>

        <div className="mobile-filters-header-row">
          <button
            type="button"
            className="add-filters-mobile-btn"
            onClick={() => setShowMobileFilters(true)}
          >
            ADD FILTERS
            {activeFilterCount > 0 && (
              <span className="add-filters-badge">{activeFilterCount}</span>
            )}
            <i className="fa fa-angle-down"></i>
          </button>

          {hasUserAppliedFilters() && (
            <div className="filtered-results-count filtered-results-count--mobile">
              <span className="filtered-results-count__text">
                {displayedPhotosCount} photo
                {displayedPhotosCount !== 1 ? "s" : ""} found
              </span>
              <button
                className="filtered-results-count__refresh"
                onClick={() => {
                  setFilters(DEFAULT_FILTERS);
                  setPage(1);
                  setSelectedIds(new Set());
                }}
                title="Clear all filters"
              >
                <img src={undoIcon} alt="Clear filters" />
              </button>
            </div>
          )}
        </div>

        <FiltersBar
          filters={filters}
          activeDropdown={activeDropdown}
          toggleDropdown={toggleDropdown}
          applyFilter={applyFilter}
          availablePeople={availablePeople}
          availableEvents={availableEvents}
          availableTags={availableTags}
          availablePermissionAttributes={availablePermissionAttributes}
          showPermissionsFilter={showPermissionsFilter}
          showPeopleFilter={showPeopleFilter}
          hasRedactedParticipants={hasRedactedParticipants}
          isLoading={isLoading}
          loadingItemId={loadingItemId}
          loadingPermissionKey={loadingPermissionKey}
          loadingFilter={loadingFilter}
          peopleActive={peopleActive}
          permissionsActive={permissionsActive}
          dateActive={dateActive}
          selfieActive={selfieActive}
          eventActive={eventActive}
          tagActive={tagActive}
          selfieSearchS3Bucket={selfieSearchS3Bucket}
          photoDateRange={photoDateRange}
        />
      </div>

      <MobileFiltersModal
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filters={filters}
        activeDropdown={activeDropdown}
        toggleDropdown={toggleDropdown}
        applyFilter={applyFilter}
        allPeople={availablePeople}
        allEvents={availableEvents}
        allTags={availableTags}
        availablePermissionAttributes={availablePermissionAttributes}
        showPermissionsFilter={showPermissionsFilter}
        showPeopleFilter={showPeopleFilter}
        hasRedactedParticipants={hasRedactedParticipants}
        isLoading={isLoading}
        loadingItemId={loadingItemId}
        loadingPermissionKey={loadingPermissionKey}
        activeFilterCount={activeFilterCount}
        peopleActive={peopleActive}
        permissionsActive={permissionsActive}
        dateActive={dateActive}
        selfieActive={selfieActive}
        eventActive={eventActive}
        tagActive={tagActive}
      />

      <ActionBar
        hasUserAppliedFilters={hasUserAppliedFilters()}
        displayedPhotosCount={displayedPhotosCount}
        onClearFilters={handleClearFilters}
        photosLength={photos.length}
        allSelected={allSelected}
        onSelectAll={handleSelectAll}
        someSelected={someSelected}
        isDownloading={isDownloading}
        onDownload={handleDownload}
        onTag={() => setShowTagModal(true)}
        onHide={handleHide}
        onDelete={() => setShowDeleteModal(true)}
        hideOrUnhideText={hideOrUnhideText()}
      />

      {!isLoading && photos.length === 0 ? (
        <div className="filtered-photos-empty">
          <p>
            No photos found
            {hasUserAppliedFilters()
              ? " for the selected filters"
              : ` for this ${getEntityTypeLabel().toLowerCase()}`}
            .
          </p>
        </div>
      ) : (
        <PhotoManagerContext.Provider
          value={
            {
              ...parentContext,
              redactionCheckEnabled:
                redactionCheckEnabled ??
                parentContext?.redactionCheckEnabled ??
                false
            } as any
          }
        >
          <PhotoGrid
            eventGroups={eventGroups}
            selectedIds={selectedIds}
            hiddenIds={hiddenIds}
            togglePhotoSelection={togglePhotoSelection}
            handlePhotoClick={handlePhotoClick}
            navigateToEvent={navigateToEvent}
            navigateToAlbum={navigateToAlbum}
            showActionTooltip={showActionTooltip}
          />

          {hasMore && (
            <div className="filtered-photos-pagination">
              <button
                className="geo-btn geo-btn--secondary geo-btn--caps"
                onClick={() => setPage(p => p + 1)}
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </PhotoManagerContext.Provider>
      )}

      {lightboxPhoto && lightboxEvent && lightboxAlbum && (
        <PhotoManagerContext.Provider
          value={
            {
              currentUserId,
              isMobile,
              redactionCheckEnabled:
                redactionCheckEnabled ??
                parentContext?.redactionCheckEnabled ??
                false,
              encodedString: null,
              refreshAlbumDetails: async () => {}
            } as any
          }
        >
          <PhotoPopup
            photo={lightboxPhoto}
            isMobile={isMobile}
            onClose={handleCloseLightbox}
            eventName={lightboxEvent.name}
            eventId={lightboxEvent.id}
            albumName={lightboxAlbum.name}
            mediaIndex={lightboxIndex}
            mediaAlbum={lightboxPhotos}
            onNavigate={handleLightboxNavigate}
            selectedAlbum={lightboxAlbum}
            fetchAlbumPhotosFunction={() => {}}
            photoCurrentPage={1}
            showActionTooltip={() => {}}
            selectedEvent={lightboxEvent}
          />
        </PhotoManagerContext.Provider>
      )}

      {showTagModal &&
        ReactDOM.createPortal(
          <BundleMediaSelect
            mediaCount={selectedIds.size}
            contentTagsAPI="/api/v1/content_tags"
            handleClose={() => setShowTagModal(false)}
            listOfMediaId={selectedMediaList}
            handlePostCall={() => {
              setShowTagModal(false);
              setSelectedIds(new Set());
              loadPhotos();
            }}
          />,
          document.body
        )}

      {showDeleteModal && (
        <DeletePhotosConfirmation
          closePopup={() => setShowDeleteModal(false)}
          deletePhotos={handleDelete}
          isDeleting={isDeleting}
        />
      )}

      {showShowreelModal && (
        <GenerateShowreelModal
          searchParams={buildShowreelSearchParams()}
          personName={entityName}
          organizationId={organizationId}
          onClose={() => setShowShowreelModal(false)}
        />
      )}
    </div>
  );
};

export default FilteredPhotosPage;
